import React, { useState, useEffect, useRef } from 'react';
import { FileText, Image, File, ExternalLink, Trash2, Sparkles, Clock, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { documentsAPI } from '@/utils/api';

const getDocumentIcon = (mime) => {
  if (mime?.includes('pdf')) return FileText;
  if (mime?.includes('image')) return Image;
  return File;
};

const getCategoryLabel = (kind) => {
  const labels = {
    pending: 'Analyzing...',
    booking_confirmation: 'Booking',
    boarding_pass_original: 'Boarding (Orig)',
    boarding_pass_actual: 'Boarding (Actual)',
    expense_receipt: 'Receipt',
    airline_response: 'Airline Response',
    claim_letter: 'Claim',
    email_correspondence: 'Email',
    delay_certificate: 'Delay Cert',
    power_of_attorney: 'PoA',
    id_document: 'ID',
    bank_statement: 'Bank',
    other: 'Other',
  };
  return labels[kind] || kind || 'Other';
};

const getCategoryVariant = (kind) => {
  const variants = {
    pending: 'warning',
    booking_confirmation: 'info',
    boarding_pass_original: 'info',
    boarding_pass_actual: 'success',
    expense_receipt: 'success',
    airline_response: 'warning',
    claim_letter: 'error',
    email_correspondence: 'secondary',
    delay_certificate: 'warning',
    power_of_attorney: 'info',
    id_document: 'secondary',
    bank_statement: 'success',
    other: 'secondary',
  };
  return variants[kind] || 'secondary';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

/**
 * A single document row with animations for pending/categorized states
 */
const DocumentRow = ({
  document,
  onDelete,
  compact = false,
  showDelete = true,
  recentlyCategorized = false,
}) => {
  const Icon = getDocumentIcon(document.mime);
  const hasAI = document.ai_confidence !== null && document.ai_confidence !== undefined;
  const isPending = document.kind === 'pending';
  const [showCategorizedAnimation, setShowCategorizedAnimation] = useState(recentlyCategorized);
  const [downloading, setDownloading] = useState(false);

  // Handle secure document download
  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (downloading) return;

    setDownloading(true);
    try {
      const response = await documentsAPI.getDownloadUrl(document.id);
      if (response.status === 'success' && response.download_url) {
        window.open(response.download_url, '_blank');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download failed:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to download this document');
      } else if (error.response?.status === 404) {
        toast.error('Document not found');
      } else {
        toast.error('Failed to download document');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Clear the categorized animation after it plays
  useEffect(() => {
    if (showCategorizedAnimation) {
      const timer = setTimeout(() => setShowCategorizedAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showCategorizedAnimation]);

  // Trigger animation when document transitions from pending to categorized
  const prevKindRef = useRef(document.kind);
  useEffect(() => {
    if (prevKindRef.current === 'pending' && document.kind !== 'pending') {
      setShowCategorizedAnimation(true);
    }
    prevKindRef.current = document.kind;
  }, [document.kind]);

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center justify-between p-2 rounded-md transition-all duration-500',
          showCategorizedAnimation && 'animate-categorized bg-green-50',
          !showCategorizedAnimation && 'bg-slate-50'
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Icon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs truncate max-w-[120px]">
            {document.file_name || 'Document'}
          </span>
        </div>
        <Badge
          variant={getCategoryVariant(document.kind)}
          className={cn(
            'text-xs flex-shrink-0',
            isPending && 'animate-pending'
          )}
        >
          {isPending && <Clock className="h-2.5 w-2.5 mr-1 animate-spin" />}
          {showCategorizedAnimation && <Check className="h-2.5 w-2.5 mr-1" />}
          {getCategoryLabel(document.kind)}
        </Badge>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border transition-all duration-500',
        showCategorizedAnimation && 'animate-categorized border-green-300 bg-green-50',
        !showCategorizedAnimation && 'bg-white border-slate-200'
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm truncate max-w-[200px]">
            {document.file_name || 'Document'}
          </p>
          <p className="text-xs text-slate-400">
            {formatDate(document.uploaded_at)}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge
          variant={getCategoryVariant(document.kind)}
          className={cn(
            'text-xs',
            isPending && 'animate-pending'
          )}
        >
          {isPending && <Clock className="h-3 w-3 mr-1 animate-spin" />}
          {showCategorizedAnimation && <Check className="h-3 w-3 mr-1" />}
          {getCategoryLabel(document.kind)}
        </Badge>

        {hasAI && !isPending && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Sparkles className="h-3.5 w-3.5 text-blue-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>AI: {Math.round(document.ai_confidence * 100)}% confident</p>
                {document.ai_reasoning && (
                  <p className="text-xs mt-1 text-slate-300">{document.ai_reasoning}</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {document.id && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 disabled:opacity-50"
            title="Download document"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5" />
            )}
          </button>
        )}

        {showDelete && onDelete && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
            onClick={() => onDelete(document)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * DocumentList - Shared component for displaying documents with animations
 *
 * @param {Array} documents - Array of document objects
 * @param {Function} onDelete - Callback when delete is confirmed (receives document)
 * @param {Function} onDocumentUpdated - Callback when a document is updated (for parent to refresh)
 * @param {boolean} compact - Use compact display mode (for case overview cards)
 * @param {boolean} showDelete - Show delete buttons
 * @param {Set} recentlyCategorizedIds - Set of document IDs that were just categorized
 * @param {string} emptyMessage - Message to show when no documents
 */
const DocumentList = ({
  documents = [],
  onDelete,
  compact = false,
  showDelete = true,
  recentlyCategorizedIds = new Set(),
  emptyMessage = 'No documents uploaded',
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete || !onDelete) return;

    setDeleting(true);
    try {
      await onDelete(documentToDelete);
      toast.success('Document deleted');
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  if (documents.length === 0) {
    return (
      <p className="text-slate-400 text-sm text-center py-4">
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
      <div className={cn('space-y-2', compact && 'space-y-1')}>
        {documents.map((doc) => (
          <DocumentRow
            key={doc.id}
            document={doc}
            onDelete={showDelete ? handleDeleteClick : undefined}
            compact={compact}
            showDelete={showDelete}
            recentlyCategorized={recentlyCategorizedIds.has(doc.id)}
          />
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{documentToDelete?.file_name}"?
              This will permanently remove the file and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentList;
export { DocumentRow, getCategoryLabel, getCategoryVariant, getDocumentIcon };
